import { useState } from 'react';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { useAdminClient } from '../context/AdminClientContext';
import { usePopulations } from '../hooks/useClientScopedData';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Users, Upload, Trash2, FileSpreadsheet, Loader2, AlertCircle, FlaskConical, X, Plus, FileUp, Info, TriangleAlert } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import SyntheticAttributesDialog from '../components/SyntheticAttributesDialog';
import PopulationPreviewDialog from '../components/PopulationPreviewDialog';
import { Sparkles } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../components/ui/tooltip';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';


// Common response scales for pretrain
const RESPONSE_SCALES = {
  likert5: ['Strongly Disagree', 'Disagree', 'Neutral', 'Agree', 'Strongly Agree'],
  likert7: ['Strongly Disagree', 'Disagree', 'Somewhat Disagree', 'Neutral', 'Somewhat Agree', 'Agree', 'Strongly Agree'],
  yesno: ['Yes', 'No'],
  satisfaction: ['Very Dissatisfied', 'Dissatisfied', 'Neutral', 'Satisfied', 'Very Satisfied'],
  likelihood: ['Very Unlikely', 'Unlikely', 'Neutral', 'Likely', 'Very Likely'],
};

export default function Populations() {
  const { user } = useAuth();
  const { activeClient } = useAdminClient();
  const { data: populations, loading, refetch } = usePopulations();
  const [uploading, setUploading] = useState(false);
  const [showUploadSuccess, setShowUploadSuccess] = useState(false);
  const [file, setFile] = useState(null);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  
  // Population detail dialog
  const [detailPopulation, setDetailPopulation] = useState(null);
  const [pretrainDialogOpen, setPretrainDialogOpen] = useState(false);
  const [selectedPopulation, setSelectedPopulation] = useState(null);
  const [pretrainForm, setPretrainForm] = useState({
    stimulus_name: '',
    scale_type: 'likert5',
    custom_scale: [],
    distribution: {},
    sample_size: '',
    notes: ''
  });
  const [pretrainSubmitting, setPretrainSubmitting] = useState(false);
  const [viewPretrainDialogOpen, setViewPretrainDialogOpen] = useState(false);
  const [viewingPretrainData, setViewingPretrainData] = useState([]);
  
  // File upload pretrain state
  const [pretrainMode, setPretrainMode] = useState('file'); // 'file' is the only supported mode now
  const [pretrainFile, setPretrainFile] = useState(null);
  const [extracting, setExtracting] = useState(false);

  // Check if user has calibration access
  const hasCalibrationAccess = user?.calibration_access || user?.is_admin;

  // Synthetic attribute expansion (admin-only + per-client feature flag)
  const [syntheticDialogOpen, setSyntheticDialogOpen] = useState(false);
  const [syntheticPopulation, setSyntheticPopulation] = useState(null);
  const syntheticEnabled = !!(user?.is_admin && activeClient?.synthetic_attributes_enabled);

  const openSyntheticDialog = (pop) => {
    setSyntheticPopulation(pop);
    setSyntheticDialogOpen(true);
  };

  // Population preview / inspector
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewPopId, setPreviewPopId] = useState(null);
  const openPreview = (pop) => {
    setPreviewPopId(pop.id);
    setPreviewOpen(true);
  };

  // Upload animation state
  const [uploadStep, setUploadStep] = useState(-1);
  const UPLOAD_STEPS = [
    'Analyzing demographics...',
    'Generating synthetic structure...',
    'Finalizing population...',
  ];

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!file) return;

    // Validate admin has selected a specific client (not "all")
    if (user?.is_admin && (!activeClient || !activeClient.id)) {
      toast.error('Please select a specific client (not "All Clients") before uploading a population');
      return;
    }

    setUploading(true);
    setUploadStep(0);
    const formData = new FormData();
    formData.append('file', file);

    // Start animation steps (minimum 2.5s total visible)
    const stepTimers = [
      setTimeout(() => setUploadStep(1), 900),
      setTimeout(() => setUploadStep(2), 1800),
    ];

    try {
      const clientParam = user?.is_admin && activeClient?.id ? `&client_id=${activeClient.id}` : '';
      await api.post(
        `/populations/upload?name=${encodeURIComponent(name)}&description=${encodeURIComponent(description)}${clientParam}`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      // Ensure minimum display time
      await new Promise(resolve => setTimeout(resolve, 2500));
      toast.success('Population uploaded successfully!');
      setFile(null);
      setName('');
      setDescription('');
      refetch();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload population');
    } finally {
      setUploading(false);
      setUploadStep(-1);
    }
  };

  // Delete confirmation state
  const [deleteTarget, setDeleteTarget] = useState(null);

  const handleDelete = async (id) => {
    try {
      await api.delete(`/populations/${id}`);
      toast.success('Population deleted');
      setDeleteTarget(null);
      refetch();
    } catch (error) {
      toast.error('Failed to delete population');
    }
  };

  const openPretrainDialog = (population) => {
    setSelectedPopulation(population);
    const defaultScale = RESPONSE_SCALES.likert5;
    setPretrainForm({
      stimulus_name: '',
      scale_type: 'likert5',
      custom_scale: [],
      distribution: Object.fromEntries(defaultScale.map(opt => [opt, 0])),
      sample_size: '',
      notes: ''
    });
    setPretrainMode('file');
    setPretrainFile(null);
    setPretrainDialogOpen(true);
  };

  const handleScaleTypeChange = (scaleType) => {
    const scale = RESPONSE_SCALES[scaleType] || [];
    setPretrainForm(prev => ({
      ...prev,
      scale_type: scaleType,
      distribution: Object.fromEntries(scale.map(opt => [opt, 0]))
    }));
  };

  const handleDistributionChange = (option, value) => {
    setPretrainForm(prev => ({
      ...prev,
      distribution: {
        ...prev.distribution,
        [option]: parseFloat(value) || 0
      }
    }));
  };

  const handleFilePretrainSubmit = async (e) => {
    e.preventDefault();
    if (!selectedPopulation || !pretrainFile || !pretrainForm.stimulus_name) return;

    setExtracting(true);
    try {
      const formData = new FormData();
      formData.append('file', pretrainFile);

      const response = await api.post(
        `/populations/${selectedPopulation.id}/pretrain/upload?stimulus_name=${encodeURIComponent(pretrainForm.stimulus_name)}`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      toast.success('Survey data extracted and added successfully');
      setPretrainDialogOpen(false);
      setPretrainFile(null);
      refetch();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to extract survey data from file');
    } finally {
      setExtracting(false);
    }
  };

  const handleSubmitPretrain = async (e) => {
    e.preventDefault();
    if (!selectedPopulation) return;

    // Validate distribution sums to ~100
    const total = Object.values(pretrainForm.distribution).reduce((a, b) => a + b, 0);
    if (total < 95 || total > 105) {
      toast.error(`Distribution must sum to 100% (currently ${total.toFixed(1)}%)`);
      return;
    }

    setPretrainSubmitting(true);
    try {
      const payload = {
        stimulus_name: pretrainForm.stimulus_name,
        response_scale: RESPONSE_SCALES[pretrainForm.scale_type] || pretrainForm.custom_scale,
        distribution: pretrainForm.distribution,
        sample_size: pretrainForm.sample_size ? parseInt(pretrainForm.sample_size) : null,
        notes: pretrainForm.notes || null
      };

      await api.post(`/populations/${selectedPopulation.id}/pretrain`, payload);
      toast.success('Pretrain data added successfully');
      setPretrainDialogOpen(false);
      refetch();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add pretrain data');
    } finally {
      setPretrainSubmitting(false);
    }
  };

  const handleViewPretrainData = async (population) => {
    try {
      const response = await api.get(`/populations/${population.id}/pretrain`);
      setViewingPretrainData(response.data.pretrain_data || []);
      setSelectedPopulation(population);
      setViewPretrainDialogOpen(true);
    } catch (error) {
      toast.error('Failed to load pretrain data');
    }
  };

  const handleDeletePretrainEntry = async (pretrainId) => {
    if (!selectedPopulation) return;
    if (!window.confirm('Delete this pretrain entry?')) return;

    try {
      await api.delete(`/populations/${selectedPopulation.id}/pretrain/${pretrainId}`);
      toast.success('Pretrain entry deleted');
      // Refresh the pretrain data
      const response = await api.get(`/populations/${selectedPopulation.id}/pretrain`);
      setViewingPretrainData(response.data.pretrain_data || []);
      refetch();
    } catch (error) {
      toast.error('Failed to delete pretrain entry');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Admin Client View Banner */}
      {user?.is_admin && activeClient && (
        <div className="flex items-center gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <AlertCircle className="w-5 h-5 text-blue-600" />
          <span className="text-sm font-medium text-blue-900">
            Viewing as: <span className="font-bold">{activeClient.name}</span>
          </span>
        </div>
      )}

      {/* Admin Warning - No Client Selected */}
      {user?.is_admin && !activeClient && (
        <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
          <AlertCircle className="w-5 h-5 text-amber-600" />
          <span className="text-sm font-medium text-amber-900">
            Please select a specific client (not "All Clients") before uploading a population
          </span>
        </div>
      )}

      <div>
        <h1 className="text-3xl font-bold text-foreground">Populations</h1>
        <p className="text-muted-foreground mt-1">Upload and manage your population data</p>
      </div>

      {/* Upload Form */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Upload New Population</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleUpload} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="name">Population Name *</Label>
                <Input
                  id="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g., Employee Survey 2024"
                  required
                />
              </div>
              <div>
                <Label htmlFor="description">Description</Label>
                <Input
                  id="description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Optional"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="file">CSV or Excel File *</Label>
              <Input
                id="file"
                type="file"
                accept=".csv,.xlsx,.xls"
                onChange={(e) => setFile(e.target.files[0])}
                required
              />
            </div>
            <Button type="submit" disabled={uploading || !file} className="bg-primary hover:bg-primary/90" data-testid="upload-population-btn">
              {uploading ? (
                <div className="flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>{uploadStep >= 0 && uploadStep < UPLOAD_STEPS.length ? UPLOAD_STEPS[uploadStep] : 'Processing...'}</span>
                </div>
              ) : (
                <><Upload className="w-4 h-4 mr-2" /> Upload Population</>
              )}
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Populations List */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {populations && populations.length > 0 ? populations.map(pop => (
          <Card key={pop.id} className={`hover:shadow-md transition-shadow ${pop.pretrained ? 'border-emerald-200 dark:border-emerald-800' : ''}`} data-testid={`population-card-${pop.id}`}>
            <CardHeader
              className="pb-3 cursor-pointer hover:bg-slate-50/40 dark:hover:bg-white/[0.02] transition-colors"
              onClick={() => openPreview(pop)}
              data-testid={`pop-open-preview-${pop.id}`}
            >
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  <Users className="w-5 h-5 text-blue-600 shrink-0" />
                  <CardTitle className="text-lg">{pop.name}</CardTitle>
                  {/* Population Info Button — kept as explicit affordance */}
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 w-6 p-0 text-muted-foreground hover:text-foreground"
                    onClick={(e) => { e.stopPropagation(); openPreview(pop); }}
                    data-testid={`pop-info-${pop.id}`}
                    title="Inspect data"
                  >
                    <Info className="w-4 h-4" />
                  </Button>
                  {pop.pretrained && (
                    <Badge
                      className="bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400 border-emerald-300 dark:border-emerald-700 cursor-pointer hover:bg-emerald-200"
                      onClick={(e) => { e.stopPropagation(); handleViewPretrainData(pop); }}
                    >
                      <FlaskConical className="w-3 h-3 mr-1" />
                      Pretrained
                    </Badge>
                  )}
                </div>
              </div>
              {pop.description && (
                <p className="text-sm text-muted-foreground mt-1">{pop.description}</p>
              )}
            </CardHeader>
            <CardContent>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Individuals:</span>
                  <span className="font-medium">{pop.row_count}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Attributes:</span>
                  <span className="font-medium">{pop.columns?.length || 0}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Created:</span>
                  <span className="font-medium">{new Date(pop.created_at).toLocaleDateString()}</span>
                </div>
              </div>
              
              <div className="mt-4 pt-3 border-t border-border flex items-center gap-2">
                {/* Survey Pretrain button - only visible to users with calibration access */}
                {hasCalibrationAccess && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => openPretrainDialog(pop)}
                    className="flex-1 text-emerald-700 dark:text-emerald-400 border-emerald-300 dark:border-emerald-700 hover:bg-emerald-50 dark:hover:bg-emerald-900/20"
                  >
                    <FlaskConical className="w-4 h-4 mr-2" />
                    {pop.pretrained ? 'Add Pretrain Data' : 'Survey Pretrain'}
                  </Button>
                )}
                {/* AI Profile Enrichment - admin-only + per-client feature flag */}
                {syntheticEnabled && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => openSyntheticDialog(pop)}
                    className="flex-1 text-blue-700 dark:text-blue-400 border-blue-300 dark:border-blue-700 hover:bg-blue-50 dark:hover:bg-blue-900/20"
                    data-testid={`synthetic-expand-${pop.id}`}
                    title="AI Profile Enrichment (admin-only)"
                  >
                    <Sparkles className="w-4 h-4 mr-2" />
                    AI Enrich
                  </Button>
                )}
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setDeleteTarget(pop)}
                  className="text-red-600 dark:text-red-400 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20"
                  data-testid={`delete-pop-${pop.id}`}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        )) : null}
      </div>

      {populations && populations.length === 0 && (
        <Card className="p-8 text-center">
          <FileSpreadsheet className="w-12 h-12 mx-auto text-slate-300 mb-4" />
          <h3 className="text-lg font-medium text-slate-900 dark:text-white mb-2">No populations yet</h3>
          <p className="text-sm text-slate-500">Upload a CSV or Excel file to get started</p>
        </Card>
      )}

      {/* Pretrain Dialog */}
      <Dialog open={pretrainDialogOpen} onOpenChange={setPretrainDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FlaskConical className="w-5 h-5 text-emerald-600" />
              Survey Pretrain
            </DialogTitle>
            <DialogDescription>
              Upload real survey data or HR research datasets to calibrate AI predictions for &quot;{selectedPopulation?.name}&quot;
            </DialogDescription>
          </DialogHeader>
          
          <Tabs value={pretrainMode} onValueChange={setPretrainMode} className="w-full">
            {/* Upload File is the only input mode */}
            <TabsContent value="file" className="space-y-4 mt-4">
              <form onSubmit={handleFilePretrainSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="file_stimulus_name">Survey/Stimulus Name *</Label>
                  <Input
                    id="file_stimulus_name"
                    value={pretrainForm.stimulus_name}
                    onChange={(e) => setPretrainForm(prev => ({ ...prev, stimulus_name: e.target.value }))}
                    placeholder="e.g., Return to Office Policy Survey"
                    required
                  />
                </div>

                <div>
                  <Label>Upload Survey Results File *</Label>
                  <div className="mt-2 border-2 border-dashed border-slate-300 rounded-lg p-6 text-center hover:border-emerald-400 transition-colors">
                    <input
                      type="file"
                      accept=".pdf,.ppt,.pptx,.csv,.xlsx,.xls,.txt"
                      onChange={(e) => setPretrainFile(e.target.files[0])}
                      className="hidden"
                      id="pretrain-file-input"
                    />
                    <label htmlFor="pretrain-file-input" className="cursor-pointer">
                      {pretrainFile ? (
                        <div className="flex items-center justify-center gap-2 text-emerald-600">
                          <FileSpreadsheet className="w-8 h-8" />
                          <div className="text-left">
                            <p className="font-medium">{pretrainFile.name}</p>
                            <p className="text-xs text-slate-500">{(pretrainFile.size / 1024).toFixed(1)} KB</p>
                          </div>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={(e) => { e.preventDefault(); setPretrainFile(null); }}
                            className="ml-2"
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      ) : (
                        <>
                          <FileUp className="w-10 h-10 mx-auto text-slate-400 mb-2" />
                          <p className="text-sm text-slate-600 font-medium">Click to upload or drag and drop</p>
                          <p className="text-xs text-slate-400 mt-1">PDF, PPT, CSV, Excel files supported</p>
                        </>
                      )}
                    </label>
                  </div>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <p className="text-xs text-blue-800">
                    <strong>AI will extract:</strong> Response options and their percentage distribution from your survey report, presentation, or raw data file.
                  </p>
                </div>

                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setPretrainDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button 
                    type="submit" 
                    disabled={extracting || !pretrainFile || !pretrainForm.stimulus_name} 
                    className="bg-emerald-600 hover:bg-emerald-700"
                  >
                    {extracting ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Extracting...</>
                    ) : (
                      <><FileUp className="w-4 h-4 mr-2" /> Extract & Add</>
                    )}
                  </Button>
                </DialogFooter>
              </form>
            </TabsContent>

            {/* Subtrain explanation */}
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3.5 mt-4" data-testid="subtrain-explanation">
              <p className="text-xs text-blue-800 dark:text-blue-300 leading-relaxed">
                <strong>How Subtraining Works:</strong> SynPop adapts the behavioral model using company-specific documents
                such as engagement surveys, policy documents, or research reports. Upload CSV, XLSX, PDF, PPT, or TXT files
                containing previous survey datasets, employee feedback data, or HR research datasets.
                The system extracts response patterns and adjusts probability distributions to better reflect your organization.
              </p>
            </div>

          </Tabs>
        </DialogContent>
      </Dialog>

      {/* View Pretrain Data Dialog */}
      <Dialog open={viewPretrainDialogOpen} onOpenChange={setViewPretrainDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FlaskConical className="w-5 h-5 text-emerald-600" />
              Pretrain Data for &quot;{selectedPopulation?.name}&quot;
            </DialogTitle>
          </DialogHeader>
          
          {viewingPretrainData.length === 0 ? (
            <p className="text-slate-500 text-center py-4">No pretrain data yet.</p>
          ) : (
            <div className="space-y-4 max-h-96 overflow-y-auto">
              {viewingPretrainData.map((entry, idx) => (
                <Card key={entry.id || idx} className="border border-slate-200">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h4 className="font-medium text-slate-900 dark:text-white">{entry.stimulus_name}</h4>
                        <p className="text-xs text-slate-500">
                          Added {new Date(entry.created_at).toLocaleDateString()}
                          {entry.sample_size && ` • Sample: ${entry.sample_size}`}
                        </p>
                      </div>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDeletePretrainEntry(entry.id)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-2">
                      {Object.entries(entry.distribution || {}).map(([option, percent]) => (
                        <div key={option} className="flex justify-between text-sm bg-slate-50 px-2 py-1 rounded">
                          <span className="text-slate-600 truncate">{option}</span>
                          <span className="font-medium">{percent.toFixed(1)}%</span>
                        </div>
                      ))}
                    </div>
                    
                    {entry.notes && (
                      <p className="text-xs text-slate-500 mt-2 italic">{entry.notes}</p>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {hasCalibrationAccess && (
            <DialogFooter>
              <Button variant="outline" onClick={() => setViewPretrainDialogOpen(false)}>
                Close
              </Button>
              <Button 
                onClick={() => {
                  setViewPretrainDialogOpen(false);
                  openPretrainDialog(selectedPopulation);
                }}
                className="bg-emerald-600 hover:bg-emerald-700"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add More Data
              </Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>

      {/* Population Detail Dialog */}
      <Dialog open={!!detailPopulation} onOpenChange={() => setDetailPopulation(null)}>
        <DialogContent className="max-w-lg" data-testid="population-detail-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="w-5 h-5 text-blue-600" />
              {detailPopulation?.name}
            </DialogTitle>
            {detailPopulation?.description && (
              <DialogDescription>{detailPopulation.description}</DialogDescription>
            )}
          </DialogHeader>
          {detailPopulation && (
            <div className="grid grid-cols-2 gap-6">
              {/* Left column */}
              <div className="space-y-4">
                <div>
                  <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Population Size</span>
                  <p className="text-2xl font-bold text-foreground">{detailPopulation.row_count?.toLocaleString()}</p>
                  <span className="text-xs text-muted-foreground">individuals</span>
                </div>
                <div>
                  <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Attributes</span>
                  <p className="text-lg font-bold text-foreground">{detailPopulation.columns?.length || 0}</p>
                  <span className="text-xs text-muted-foreground">demographic fields</span>
                </div>
                <div>
                  <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Created</span>
                  <p className="text-sm font-medium text-foreground">
                    {new Date(detailPopulation.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                  </p>
                </div>
                <div>
                  <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Pretrain Status</span>
                  <p className="mt-1">
                    {detailPopulation.pretrained ? (
                      <Badge className="bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400 border-emerald-300 dark:border-emerald-700">
                        <FlaskConical className="w-3 h-3 mr-1" /> Pretrained
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="text-muted-foreground">Not pretrained</Badge>
                    )}
                  </p>
                </div>
              </div>
              {/* Right column — Attribute list */}
              <div>
                <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Demographic Attributes</span>
                <div className="mt-2 max-h-[280px] overflow-y-auto space-y-1.5 pr-2">
                  {detailPopulation.columns?.map((col) => (
                    <div key={col.name} className="flex items-center justify-between p-2 bg-muted rounded-lg border border-border">
                      <span className="text-sm font-medium text-foreground">{col.name}</span>
                      {col.unique_values && (
                        <Badge variant="outline" className="text-[10px] text-muted-foreground">
                          {col.unique_values} values
                        </Badge>
                      )}
                    </div>
                  ))}
                  {(!detailPopulation.columns || detailPopulation.columns.length === 0) && (
                    <p className="text-xs text-muted-foreground italic">No attribute data available</p>
                  )}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <TriangleAlert className="w-5 h-5 text-red-500" />
              Delete Population
            </AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete <strong>{deleteTarget?.name}</strong>? 
              This will permanently remove the population and all associated data. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => handleDelete(deleteTarget?.id)}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="confirm-delete-pop-btn"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Synthetic Attribute Expansion Dialog (admin-only, feature-gated) */}
      <SyntheticAttributesDialog
        open={syntheticDialogOpen}
        onOpenChange={setSyntheticDialogOpen}
        population={syntheticPopulation}
      />

      {/* Population Inspector / Preview */}
      <PopulationPreviewDialog
        open={previewOpen}
        onOpenChange={setPreviewOpen}
        populationId={previewPopId}
      />
    </div>
  );
}
